import { shiftDate, shiftMonths, today } from '../../core/format/dates'
import { toCents } from '../../core/format/currency'
import { validUntilFor, voucherCode } from '../../core/rules/ledger'
import { policy } from '../../data/policies'
import { isGiftCard } from '../pricing'
import type { CreditRow, CustomerRow, OrderRow, VoucherRow } from '../db'

/**
 * DEMO — cadeaubonnen en tegoed voor de eerste keer laden. Elke bon die in de voorbeeldorders
 * verkocht is, bestaat hier ook (zoals de webhook hem na betalen aanmaakt), plus een paar uit de
 * salon: deels gebruikt, alleen voor behandelingen, leeg, en één die net verlopen is.
 */

/** Vast "toeval", zodat de codes bij elke opbouw hetzelfde zijn. */
function seeded(seed: number) {
  let s = seed >>> 0
  return () => {
    s = (s + 0x6d2b79f5) >>> 0
    let t = s
    t = Math.imul(t ^ (t >>> 15), t | 1)
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61)
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }
}

const at = (date: string, time = '10:00') => `${date}T${time}`

export function seedVouchers(orders: OrderRow[]): VoucherRow[] {
  const random = seeded(4031)
  const codes = new Set<string>()
  const code = () => {
    let c = voucherCode(random)
    while (codes.has(c)) c = voucherCode(random)
    codes.add(c)
    return c
  }
  const months = policy.giftCardValidMonths
  let n = 0
  const id = () => `bon-${String(++n).padStart(3, '0')}`

  const fromWebshop = orders
    .filter((o) => o.payment.status === 'betaald' && !o.cancelledAt)
    .flatMap((o) =>
      o.lines
        .filter((l) => isGiftCard(l.productId))
        .flatMap((l) =>
          Array.from({ length: l.qty }, (): VoucherRow => {
            const createdAt = o.paidAt ?? o.receivedAt
            const vid = id()
            return {
              id: vid,
              code: code(),
              initialCents: toCents(l.price),
              validUntil: validUntilFor(createdAt.slice(0, 10), months),
              validFor: 'all',
              source: 'webshop',
              createdAt,
              buyerName: o.name,
              orderId: o.id,
              note: '',
              ledger: [{ id: `${vid}-1`, at: createdAt, kind: 'uitgegeven', unit: 'cents', amount: toCents(l.price) }],
            }
          }),
        ),
    )

  const d = today()
  const salon = (
    createdOn: string,
    initialCents: number,
    validFor: VoucherRow['validFor'],
    source: VoucherRow['source'],
    note: string,
    used: { on: string; cents: number; note: string }[] = [],
    buyerName?: string,
  ): VoucherRow => {
    const vid = id()
    return {
      id: vid,
      code: code(),
      initialCents,
      validUntil: validUntilFor(createdOn, months),
      validFor,
      source,
      createdAt: at(createdOn),
      ...(buyerName ? { buyerName } : {}),
      note,
      ledger: [
        { id: `${vid}-1`, at: at(createdOn), kind: 'uitgegeven', unit: 'cents', amount: initialCents },
        ...used.map((u, i) => ({ id: `${vid}-${i + 2}`, at: at(u.on, '14:30'), kind: 'ingewisseld' as const, unit: 'cents' as const, amount: -u.cents, note: u.note })),
      ],
    }
  }

  return [
    ...fromWebshop,
    salon(shiftDate(d, -60), 5000, 'all', 'handmatig', 'Winactie Instagram (september)', [{ on: shiftDate(d, -20), cents: 1250, note: 'Ingewisseld in de salon' }]),
    salon(shiftMonths(d, -5), 7500, 'treatments', 'kassa', '', [], 'Marieke Jansen'),
    salon(shiftMonths(d, -9), 5000, 'all', 'kassa', '', [{ on: shiftMonths(d, -6), cents: 5000, note: 'Ingewisseld in de salon' }], 'Joyce de Boer'),
    // Net verlopen met saldo: de demo boekt het restant bij de eerste keer openen af ("vervallen").
    salon(shiftMonths(d, -months - 1), 2500, 'treatments', 'kassa', '', [], 'Annemiek Bos'),
  ].sort((a, b) => b.createdAt.localeCompare(a.createdAt))
}

/** Tegoed bij twee voorbeeldklanten: een behandeling (coulance) en een overgezet bedrag. */
export function seedCredits(customers: CustomerRow[]): CreditRow[] {
  const [first, second] = customers
  const d = today()
  return [
    ...(first
      ? [{ id: 'tegoed-001', customerId: first.customer.id, at: at(shiftDate(d, -21), '16:10'), kind: 'correctie' as const, unit: 'treatment' as const, amount: 1, note: 'Coulance: behandeling ingekort door storing apparaat' }]
      : []),
    ...(second
      ? [{ id: 'tegoed-002', customerId: second.customer.id, at: at(shiftDate(d, -45), '09:00'), kind: 'correctie' as const, unit: 'cents' as const, amount: 1500, note: 'Overgezet tegoed uit WooCommerce' }]
      : []),
  ]
}
